import React, { useState } from 'react'
import { Modal } from '../../../components/Modal/Modal'
import { Col, Row } from 'react-bootstrap'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { Button } from '../../../components/Button/Button'
import { AIAgentResult, AIAgentType } from '../../../models/AIAgent'
import { useLazyGetAiAgentResultQuery } from '../../../store/ai/aiAgentResult'
import { useExecuteAiAgentMutation } from '../../../store/ai/aiAgent'
import { AIAgentLoader } from '../AIAgentLoader/AIAgentLoader'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { FormSelector } from './FormSelector'
import { FormFillBody } from './FormFillBody'

interface FormFillModalProps {
  contactId: string
  onHide: () => void
  show: boolean
}

export const FormFillModal: React.FC<FormFillModalProps> = ({ contactId, onHide, show }) => {
  const { tracked } = useTracking()
  const accountId = useAppSelector(selectAccountId)

  const [aiFeatureId, setAiFeatureId] = useState<string | null>(null)
  const [scorecardId, setScorecardId] = useState<string | null>(null)
  const [showFormSelector, setShowFormSelector] = useState<boolean>(true)
  const [displayForm, setDisplayForm] = useState<AIAgentResult | null>(null)
  const [isLoading, setLoading] = useState<boolean>(false)

  const [triggerGetAiAgentResult] = useLazyGetAiAgentResultQuery()
  const [triggerExecuteAiAgent] = useExecuteAiAgentMutation()

  const _executeForm = async (aiFeatureId: string, scorecardId: string) => {
    setLoading(true)
    try {
      await triggerExecuteAiAgent({
        accountId,
        payload: {
          type: AIAgentType.CLIENT_FORM,
          context: {
            contactId: contactId,
            scorecardId: scorecardId,
            aiFeatureId: aiFeatureId,
          },
        },
      }).unwrap()
      const formResult = await getFormResult(aiFeatureId)
      if (formResult) {
        setDisplayForm(formResult)
      } else {
        setShowFormSelector(true)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const executeForm = tracked(_executeForm, {
    eventName: TrackEvent.CONTACT_FORM_EXECUTED,
    eventDetails: {
      aiFeatureId,
      contactId,
    },
  })

  const getFormResult = async (aiFeatureId: string) => {
    const formResult = await triggerGetAiAgentResult(
      {
        accountId,
        type: AIAgentType.CLIENT_FORM,
        resultKey: `contactForm|${aiFeatureId}|${contactId}`,
      },
      false,
    ).unwrap()
    return formResult as AIAgentResult | null
  }

  const handleFormSelected = async (selectedAiFeatureId: string, selectedScorecardId: string) => {
    setShowFormSelector(false)
    setAiFeatureId(selectedAiFeatureId)
    setScorecardId(selectedScorecardId)
    const existingFormResult = await getFormResult(selectedAiFeatureId)
    if (existingFormResult) {
      setDisplayForm(existingFormResult)
    } else {
      await executeForm(selectedAiFeatureId, selectedScorecardId)
    }
  }

  const handleReturn = () => {
    setShowFormSelector(true)
    setDisplayForm(null)
  }

  const _handleRerun = async () => {
    if (!aiFeatureId || !scorecardId) {
      return
    }
    await _executeForm(aiFeatureId, scorecardId)
  }

  const handleRerun = tracked(_handleRerun, {
    eventName: TrackEvent.CONTACT_FORM_RERUN,
    eventDetails: {
      aiFeatureId: aiFeatureId,
      contactId: contactId,
    },
  })

  const FooterComponent: React.FC = () => {
    return (
      <Row className='pb-4'>
        <Col className='p-0 d-flex align-items-center justify-content-between'>
          <div className='d-flex align-items-center gap-3'>
            <Button className='button-medium py-0' onClick={handleReturn}>
              <i className='bi bi-arrow-return-left' />
              Choose a different form
            </Button>
            <Button className='button-medium py-0' onClick={handleRerun}>
              <i className='bi bi-arrow-clockwise' />
              Rerun
            </Button>
          </div>
        </Col>
      </Row>
    )
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      size={!showFormSelector ? 'xl' : undefined}
      header={
        <FormFillModalHeader
          toolTipTitle={
            showFormSelector
              ? 'You can select a form from the list that the AI Assistant will populate with information based on past meetings and your CRM.'
              : 'You can manually edit the form fields and save it to your CRM system connected.'
          }
        />
      }
      body={
        showFormSelector ? (
          <FormSelector handleFormSelected={handleFormSelected} />
        ) : displayForm && aiFeatureId ? (
          <FormFillBody
            isLoading={isLoading}
            formResult={displayForm}
            contactId={contactId}
            aiFeatureId={aiFeatureId}
            FooterComponent={FooterComponent}
          ></FormFillBody>
        ) : (
          <AIAgentLoader withChat={false} />
        )
      }
    />
  )
}

const FormFillModalHeader = ({ toolTipTitle }: { toolTipTitle: string }) => {
  return (
    <Col className='d-flex gap-2 align-items-center justify-content-center'>
      <h4>Form Fill</h4>
      <Tooltip className='zocks-text-gray-500' placement='right' title={toolTipTitle}>
        <i className='bi bi-info-circle' />
      </Tooltip>
    </Col>
  )
}
